import {
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  getApiEncounterGetEncounterSummariesByDate,
  getApiEncounterGetEncounterDetailsById,
} from 'endpoints';
import {
  EncounterViewModel,
  EncounterDetailsViewModel,
} from 'dtos';
import {
  EncounterNotificationDTO,
} from 'dtos/encounterNotificationDTO';
import keyBy from 'lodash/keyBy';
import useSignalREffect from 'views/EMR/hooks/signalR';
import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import {
  useContext,
  useMemo,
} from 'react';
import {
  FilterContext,
} from 'core/Filters/context';
import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';

export function useEncounters(date: string): UseQueryResult<EncounterViewModel[], Error> {
  return useQuery<EncounterViewModel[], Error>({
    queryKey: ['encounters', date],
    queryFn: ({ signal }) => getApiEncounterGetEncounterSummariesByDate(
      { date },
      { signal },
    ),
  });
}

export function useEncounterDetails(encounterId: number):
UseQueryResult<EncounterDetailsViewModel, Error> {
  return useQuery<EncounterDetailsViewModel, Error>({
    queryKey: ['encounter-details', encounterId],
    queryFn: ({ signal }) => getApiEncounterGetEncounterDetailsById(
      { encounterId },
      { signal },
    ),
  });
}

const encounterProperties = [
  'durationInMinutes',
  'patientAppointmentStatus',
  'typeOfVisitDescription',
  'reasonForVisit',
  'canBeRescheduledSooner',
  'startDateTime',
];

export function useEncounterNotificationEffect(date: Dayjs) {
  const {
    data: allEncounters,
    refetch,
  } = useContext(FilterContext);

  const encounterByEncounterId = useMemo(() => (
    keyBy(allEncounters, 'encounterId')
  ), [allEncounters]);

  useSignalREffect('OnEncounterUpdated', (data: EncounterNotificationDTO) => {
    const { encounterId } = data;
    const existingEncounter = encounterByEncounterId[encounterId ?? ''];
    const isEncounterInView = !isNil(existingEncounter);

    const isDataChanged = isEncounterInView
      && !isEqual(
        pick(existingEncounter, encounterProperties),
        pick(data, encounterProperties),
      );

    const isDateChangedToCurrentDay = !isEncounterInView
      && dayjs(data.startDateTime).isSame(date, 'day');

    if (isDataChanged || isDateChangedToCurrentDay) {
      refetch?.();
    }
  }, [encounterByEncounterId]);

  useSignalREffect('OnEncounterDeleted', (data: EncounterNotificationDTO) => {
    const { encounterId } = data;
    const existingEncounter = encounterByEncounterId[encounterId ?? ''];

    if (!isNil(existingEncounter)) {
      refetch?.();
    }
  }, [encounterByEncounterId]);

  useSignalREffect('OnEncounterAdded', (data: EncounterNotificationDTO) => {
    const { startDateTime } = data;
    if (dayjs(startDateTime).isSame(date, 'day')) {
      refetch?.();
    }
  }, [date]);
}
